// Tipos y configuraciones para gráficas personalizadas

// Configuración por defecto para medidores
export const CONFIGURACION_MEDIDOR_DEFAULT = {
  mostrarLimites: false,
  colorBarra: '#d80303',
  mostrarPorcentaje: false,
  formatoValor: 'decimal'
};

// Configuración por defecto para líneas
export const CONFIGURACION_LINEAS_DEFAULT = {
  mostrarPromedio: true,
  mostrarTendencia: true,
  periodoHistorico: 30,
  colorLinea: '#2196F3',
  mostrarPuntos: true,
  suavizado: true
};

// Tipos de gráficas disponibles
export const TIPOS_GRAFICA = {
  MEDIDOR: 'medidor',
  LINEAS: 'lineas'
};

// Tipos de agregación
export const TIPOS_AGREGACION = {
  SERIE_HISTORICA: 'serie_historica',
  PROMEDIO_DIARIO: 'promedio_diario'
};

// Estados de alerta para medidores
export const ESTADOS_ALERTA = {
  NORMAL: 'normal',
  ADVERTENCIA: 'advertencia',
  PELIGRO: 'peligro'
};

// Formatos de valor
export const FORMATOS_VALOR = {
  DECIMAL: 'decimal',
  ENTERO: 'entero',
  CIENTIFICO: 'cientifico'
};

// Tendencias para líneas
export const TENDENCIAS = {
  ASCENDENTE: 'ascendente',
  DESCENDENTE: 'descendente',
  ESTABLE: 'estable'
};

// Función para crear una gráfica por defecto
export const crearGraficaDefault = (tipo = TIPOS_GRAFICA.MEDIDOR) => ({
  titulo: '',
  descripcion: '',
  tipoGrafica: tipo,
  tipoAgregacion: tipo === TIPOS_GRAFICA.MEDIDOR ? TIPOS_AGREGACION.SERIE_HISTORICA : TIPOS_AGREGACION.PROMEDIO_DIARIO,
  sensor: null,
  configuracion: tipo === TIPOS_GRAFICA.MEDIDOR ? CONFIGURACION_MEDIDOR_DEFAULT : CONFIGURACION_LINEAS_DEFAULT,
  activa: true,
  orden: 0,
  fechaCreacion: new Date().toISOString(),
  fechaActualizacion: new Date().toISOString()
});

// Función para validar configuración de medidor
export const validarConfiguracionMedidor = (config) => {
  const errores = [];
  
  if (typeof config.mostrarLimites !== 'boolean') {
    errores.push('mostrarLimites debe ser un booleano');
  }
  
  if (typeof config.colorBarra !== 'string' || !config.colorBarra.match(/^#[0-9A-F]{6}$/i)) {
    errores.push('colorBarra debe ser un color hexadecimal válido');
  }
  
  if (typeof config.mostrarPorcentaje !== 'boolean') {
    errores.push('mostrarPorcentaje debe ser un booleano');
  }
  
  if (!Object.values(FORMATOS_VALOR).includes(config.formatoValor)) {
    errores.push('formatoValor debe ser un formato válido');
  }
  
  return errores;
};

// Función para validar configuración de líneas
export const validarConfiguracionLineas = (config) => {
  const errores = [];
  
  if (typeof config.mostrarPromedio !== 'boolean') {
    errores.push('mostrarPromedio debe ser un booleano');
  }
  
  if (typeof config.mostrarTendencia !== 'boolean') {
    errores.push('mostrarTendencia debe ser un booleano');
  }
  
  if (typeof config.periodoHistorico !== 'number' || config.periodoHistorico < 7 || config.periodoHistorico > 90) {
    errores.push('periodoHistorico debe ser un número entre 7 y 90');
  }
  
  if (typeof config.colorLinea !== 'string' || !config.colorLinea.match(/^#[0-9A-F]{6}$/i)) {
    errores.push('colorLinea debe ser un color hexadecimal válido');
  }
  
  if (typeof config.mostrarPuntos !== 'boolean') {
    errores.push('mostrarPuntos debe ser un booleano');
  }
  
  if (typeof config.suavizado !== 'boolean') {
    errores.push('suavizado debe ser un booleano');
  }
  
  return errores;
};

// Función para obtener el estado de alerta basado en el valor
export const obtenerEstadoAlerta = (valor, rangoMin, rangoMax) => {
  // Validar que los parámetros sean números válidos
  if (typeof valor !== 'number' || isNaN(valor) ||
      typeof rangoMin !== 'number' || isNaN(rangoMin) ||
      typeof rangoMax !== 'number' || isNaN(rangoMax) ||
      rangoMin >= rangoMax) {
    return ESTADOS_ALERTA.NORMAL; // Estado por defecto para datos inválidos
  }

  const rango = rangoMax - rangoMin;
  const porcentaje = ((valor - rangoMin) / rango) * 100;

  // Umbrales ajustados según especificaciones:
  // normal: valor entre 20% y 80% del rango
  // advertencia: valor ≤ 20% del rango
  // peligro: valor ≥ 80% del rango
  if (porcentaje >= 80) {
    return ESTADOS_ALERTA.PELIGRO;
  } else if (porcentaje <= 20) {
    return ESTADOS_ALERTA.ADVERTENCIA;
  } else {
    return ESTADOS_ALERTA.NORMAL;
  }
};

// Función para formatear valores según el formato especificado
export const formatearValor = (valor, formato, decimales = 1) => {
  if (typeof valor !== 'number' || isNaN(valor)) {
    return 'N/A';
  }
  
  switch (formato) {
    case FORMATOS_VALOR.ENTERO:
      return Math.round(valor).toString();
    case FORMATOS_VALOR.CIENTIFICO:
      return valor.toExponential(decimales);
    case FORMATOS_VALOR.DECIMAL:
    default:
      return valor.toFixed(decimales);
  }
};

// Función para determinar la tendencia de una serie de datos
export const calcularTendencia = (datos) => {
  if (!datos || datos.length < 2) {
    return TENDENCIAS.ESTABLE;
  }
  
  const primerTercio = datos.slice(0, Math.floor(datos.length / 3));
  const ultimoTercio = datos.slice(-Math.floor(datos.length / 3));
  
  const promedioPrimero = primerTercio.reduce((sum, val) => sum + val, 0) / primerTercio.length;
  const promedioUltimo = ultimoTercio.reduce((sum, val) => sum + val, 0) / ultimoTercio.length;
  
  const diferencia = promedioUltimo - promedioPrimero;
  const umbral = (Math.max(...datos) - Math.min(...datos)) * 0.05; // 5% del rango
  
  if (diferencia > umbral) {
    return TENDENCIAS.ASCENDENTE;
  } else if (diferencia < -umbral) {
    return TENDENCIAS.DESCENDENTE;
  } else {
    return TENDENCIAS.ESTABLE;
  }
};